import useAuthContext from "contexts/Auth/useAuthContext";
import useTempFile from "hooks/useTempFile";
import {FC, useState} from "react";
import {handleError} from "helpers/errors";
import accountantApi from "api/accountant";
import api from "api";
import {maybeNotSetupRef} from "routes/private";
import {signInWithEmailAndPassword} from "firebase/auth";
import firebase from "config/firebase";
import {IUser} from "api/types";

const SetupPage: FC = () => {
  const auth = useAuthContext();
  const {progress} = useTempFile(`/users/${auth?.uid}/profile`);
  const [name, setName] = useState(auth?.displayName || "");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onSignup = () => {
    if (submitting) return;
    if (!name) return handleError("Please enter your name");
    if (!password) return handleError("Please enter your password");
    if (!confirmPassword) return handleError("Please confirm your password");
    if (password !== confirmPassword) return handleError("Passwords do not match");
    if (progress && progress !== 1) {
      return handleError("Please wait for your profile image to upload");
    }

    setSubmitting(true);

    accountantApi
      .signup({
        name,
        password,
        bio: "",
        location: "",
        employmentType: "",
        certifications: {},
        softwareExperience: {},
      })
      .then(async () => {
        // Re-authenticate with the newly set password — setting a password
        // on the account invalidates the magic-link custom token session,
        // which would leave the user with a broken/blank session after setup.
        if (auth?.email) {
          await signInWithEmailAndPassword(firebase.auth, auth.email, password).catch(() => {});
        }
        // Fetch full profile — the setup response is partial and missing fields like skillsEarned
        const fullProfile = await api.getProfile();
        maybeNotSetupRef.current?.setUser?.({...fullProfile, justSignedUp: true} as IUser);
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <div className="w-full bg-slate-100 h-full overflow-y-auto flex justify-center">
      <div className="bg-white rounded-3xl divide-y divide-y-slate-200 flex-cool h-max my-12 mx-4 max-w-[768px]">
        <header className="flex items-center justify-start gap-2.5 p-8">
          <img src="/akadian-logo-no-tagline.png" alt="Akadian" className="h-8 w-auto mb-4" />
          <span className="font-semibold text-2xl text-slate-900">| Create an Account</span>
        </header>
        <main className="flex-cool p-8 gap-8">
          <div className="flex-cool gap-4">
            <h3 className="font-medium text-base text-slate-900">Account Details</h3>
            <div className="flex flex-col gap-4 relative z-[100000]">
              <input type="text" placeholder="Name" value={name} onChange={(e) => setName(e.currentTarget.value)} />
              <input type="text" placeholder="Email" disabled value={auth?.email || ""} />
              <div className="relative w-full">
                <input
                  type={passwordVisible ? "text" : "password"}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.currentTarget.value)}
                  className="w-full"
                />
                <span
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-500 cursor-pointer select-none"
                  onClick={() => setPasswordVisible(!passwordVisible)}
                >
                  {passwordVisible ? "Hide" : "Show"}
                </span>
              </div>
              <div className="relative w-full">
                <input
                  type={confirmPasswordVisible ? "text" : "password"}
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.currentTarget.value)}
                  className="w-full"
                />
                <span
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-500 cursor-pointer select-none"
                  onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)}
                >
                  {confirmPasswordVisible ? "Hide" : "Show"}
                </span>
              </div>
            </div>
          </div>
        </main>
        <footer className="p-8 flex items-center justify-between gap-4">
          <span className="text-sm text-slate-500 min-w-max">
            By signing up, you agree to
            <br />
            our Terms of Service and Privacy Policy
          </span>
          <button disabled={submitting} onClick={onSignup} className="btn-primary">
            Create an Account
          </button>
        </footer>
      </div>
    </div>
  );
};

export default SetupPage;
